import React, { createContext, useContext, useMemo } from "react"
import { usePage } from "@inertiajs/react"

import { useLocationRealtime } from "@/hooks/use-location-realtime"
import {
  useNotificationFeed,
  type NotificationItem,
} from "@/hooks/use-notification-feed"

/**
 * The staff app is realtime while someone is working service, and quiet in the
 * management screens: a supplier list has nothing to refresh and nobody to
 * notify.
 *
 * Stated as "not under /manage" rather than as a list of routes. The two lists
 * this replaced disagreed with each other — the cable ran on `/tables` and
 * `/bills`, the notification feed on `/dashboard`, `/orders` and `/menu` — so
 * every screen had one wire, the wrong one, or neither. There is one gate now
 * because there is one decision: is this person on the floor.
 *
 * Exact-then-prefix for the same reason `isActiveHref` does it: `/manage` is a
 * prefix of every management route and of nothing else.
 */
export function staffRealtimeEnabledFor(path: string): boolean {
  return path !== "/manage" && !path.startsWith("/manage/")
}

type StaffNotificationsFeed = {
  notifications: NotificationItem[]
  connectionMode: "realtime" | "polling"
}

/**
 * What a consumer rendered outside the staff layout sees. `connectionMode`
 * repeats the pre-connection value rather than describing reality: nothing is
 * connected here, so a bell mounted bare in Storybook reads "Tiempo real" while
 * listening to nothing. That is a cosmetic lie confined to the workshop, and
 * the alternative — throwing "must be used within a provider" — would make both
 * surfaces impossible to look at in isolation.
 */
const EMPTY: StaffNotificationsFeed = { notifications: [], connectionMode: "realtime" }

const StaffNotificationsContext = createContext<StaffNotificationsFeed | null>(null)

/**
 * Owns every realtime wire the staff app has: the `Staff::LocationChannel`
 * subscription that refreshes stale lists, and the notification feed behind the
 * bell and the mobile unread dot.
 *
 * They are one provider because they are one subscription. The feed used to
 * hold an EventSource of its own against `/notifications/stream`, which is not
 * a stream: the endpoint runs four `FeedService` queries over 0.75s, closes,
 * and tells the browser to reconnect in ten seconds. That is a poller costing
 * 2.6x what the polling fallback costs, disconnected 93% of the time, while the
 * bell reports "Tiempo real" — and the cable was already open on the same page,
 * already carrying every event the feed was asking about.
 *
 * So the cable carries the news and the feed fetches the payload: one request
 * per event that actually happened, instead of four every eleven seconds per
 * staff device asking whether one had. When the cable drops, the feed polls on
 * its own until it comes back.
 */
export function StaffRealtimeProvider({ children }: { children: React.ReactNode }) {
  const { url, props } = usePage<{ currentLocation?: { id: number } | null }>()
  const path = url.split("?")[0]
  const locationId = props.currentLocation?.id
  const enabled = Boolean(locationId) && staffRealtimeEnabledFor(path)

  const { connectionMode, notifySignal } = useLocationRealtime({
    enabled,
    locationId,
    path: url,
  })

  const { notifications } = useNotificationFeed({
    feedPath: "/notifications/feed",
    streamPath: "/notifications/stream",
    enabled,
    // The feed's transport follows the cable's health rather than guessing at
    // its own: there is no point opening a fallback poller while the wire that
    // would tell us to fetch is up.
    transport: connectionMode === "realtime" ? "push" : "polling",
    pushSignal: notifySignal,
  })

  const value = useMemo(
    () => ({ notifications, connectionMode }),
    [notifications, connectionMode],
  )

  return (
    <StaffNotificationsContext.Provider value={value}>
      {children}
    </StaffNotificationsContext.Provider>
  )
}

export function useStaffNotifications(): StaffNotificationsFeed {
  return useContext(StaffNotificationsContext) ?? EMPTY
}
